import { Button, Tooltip } from "antd";
import { RightOutlined, SyncOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useChatStore } from "@stores/chatStore";
import {
  useAppDispatch,
  useAppSelector,
  store,
  type RootState,
} from "@stores/redux/store";
import type { ChannelType } from "@interfaces/chat.interface";
import { PLATFORM_GROUPS } from "@constants/platforms";
import { CHANNELS, PLATFORM_IDS } from "@common";
import { useMemo, useState, useCallback, useEffect, useRef } from "react";
import { formatUnreadCount } from "@utils/formatUnreadCount";
import classNames from "classnames";
import styles from "./styles.module.less";
import { clearMessages } from "@features/messages/messagesSlice";
import { fetchZaloOAs } from "@features/channels/zaloSlice";
import { fetchFacebookPages } from "@features/channels/fanpageSlice";

interface ChannelMenuProps {
  isCollapsed?: boolean;
  handleToggleFanpageMenu?: (e: React.MouseEvent) => void;
}

type FanpageItem = {
  id: string;
  name: string;
  channelType: ChannelType;
  unreadCount: number;
  messageSyncStatus?: "NOT_SYNCED" | "IN_PROGRESS" | "SYNCED";
};

export const ChannelMenu = ({
  isCollapsed = false,
  handleToggleFanpageMenu,
}: ChannelMenuProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const selectedChannel = useChatStore((state) => state.selectedChannel);
  const selectedFanpageName = useChatStore(
    (state) => state.selectedFanpageName
  );
  const selectFanpage = useChatStore((state) => state.selectFanpage);
  const selectConversation = useChatStore((state) => state.selectConversation);
  const setIsSwitchingFanpage = useChatStore(
    (state) => state.setIsSwitchingFanpage
  );
  const conversations = useChatStore((state) => state.conversations);
  const { pages: facebookPages, loading: fanpagesLoading } = useAppSelector(
    (state: RootState) => state.fanpage
  );
  const { oas: zaloOAs, loading: zaloOAsLoading } = useAppSelector(
    (state: RootState) => state.zalo
  );

  const conversationUnreadCountMap = useChatStore(
    (state) => state.conversationUnreadCountMap
  );
  const conversationPageMap = useChatStore(
    (state) => state.conversationPageMap
  );

  const fanpagesByPlatform = useMemo(() => {
    const result: Record<string, FanpageItem[]> = {};

    PLATFORM_GROUPS.forEach((group) => {
      if (group.id === PLATFORM_IDS.FACEBOOK) {
        result[group.id] = facebookPages.map((page) => {
          const pageConversations = conversations.filter(
            (conv) =>
              conv.fanpageName === page.name &&
              conv.channel === CHANNELS.FACEBOOK
          );
          const unreadFromState = pageConversations.reduce(
            (sum, conv) => sum + conv.unreadCount,
            0
          );

          let unreadFromMap = 0;
          Object.entries(conversationUnreadCountMap).forEach(
            ([convId, unreadCount]) => {
              const mappedPageId = conversationPageMap[convId];
              if (mappedPageId === page.id) {
                const convInState = pageConversations.find(
                  (conv) => conv.id === convId
                );
                if (!convInState && unreadCount > 0) {
                  unreadFromMap += unreadCount;
                }
              }
            }
          );

          const totalUnread = unreadFromState + unreadFromMap;

          return {
            id: page.id,
            name: page.name,
            channelType: CHANNELS.FACEBOOK as ChannelType,
            unreadCount: totalUnread,
            messageSyncStatus: page.messageSyncStatus,
          };
        });
      } else if (group.id === PLATFORM_IDS.ZALO) {
        result[group.id] = zaloOAs.map((oa) => {
          const oaConversations = conversations.filter(
            (conv) =>
              conv.fanpageName === oa.name && conv.channel === CHANNELS.ZALO
          );
          const unreadFromState = oaConversations.reduce(
            (sum, conv) => sum + conv.unreadCount,
            0
          );

          let unreadFromMap = 0;
          Object.entries(conversationUnreadCountMap).forEach(
            ([convId, unreadCount]) => {
              const mappedOaId = conversationPageMap[convId];
              if (mappedOaId === oa.id) {
                const convInState = oaConversations.find(
                  (conv) => conv.id === convId
                );
                if (!convInState && unreadCount > 0) {
                  unreadFromMap += unreadCount;
                }
              }
            }
          );

          const totalUnread = unreadFromState + unreadFromMap;

          return {
            id: oa.id,
            name: oa.name,
            channelType: CHANNELS.ZALO as ChannelType,
            unreadCount: totalUnread,
          };
        });
      } else {
        const platformConversations = conversations.filter((conv) =>
          group.channelTypes.includes(conv.channel)
        );
        const grouped = platformConversations.reduce((acc, conv) => {
          const key = conv.fanpageName || conv.name;
          if (!acc[key]) {
            acc[key] = {
              id: conv.id,
              name: key,
              channelType: conv.channel,
              unreadCount: 0,
            };
          }
          const persistedUnread = conversationUnreadCountMap[conv.id];
          const unreadToAdd =
            persistedUnread !== undefined && persistedUnread > conv.unreadCount
              ? persistedUnread
              : conv.unreadCount;
          acc[key].unreadCount += unreadToAdd;
          return acc;
        }, {} as Record<string, FanpageItem>);
        result[group.id] = Object.values(grouped);
      }
    });

    return result;
  }, [
    conversations,
    facebookPages,
    zaloOAs,
    conversationUnreadCountMap,
    conversationPageMap,
  ]);

  const activeKeys = useMemo(() => {
    if (location.pathname.startsWith("/settings")) {
      return ["setting"];
    }
    return PLATFORM_GROUPS.filter((group) => {
      const fanpages = fanpagesByPlatform[group.id] || [];
      return fanpages.some(
        (fanpage) =>
          selectedFanpageName === fanpage.name &&
          selectedChannel === fanpage.channelType
      );
    }).map((group) => group.id);
  }, [
    fanpagesByPlatform,
    selectedFanpageName,
    selectedChannel,
    location.pathname,
  ]);

  const [manuallyExpandedKeys, setManuallyExpandedKeys] = useState<Set<string>>(
    new Set()
  );
  const prevActiveKeysRef = useRef<string[]>([]);

  const selectedFanpageGroupId = useMemo(() => {
    if (
      selectedFanpageName &&
      !location.pathname.startsWith("/settings") &&
      !fanpagesLoading &&
      facebookPages.length > 0
    ) {
      const group = PLATFORM_GROUPS.find((g) => {
        const fanpages = fanpagesByPlatform[g.id] || [];
        return fanpages.some(
          (fanpage) =>
            selectedFanpageName === fanpage.name &&
            selectedChannel === fanpage.channelType
        );
      });
      return group?.id;
    }
    return undefined;
  }, [
    selectedFanpageName,
    selectedChannel,
    fanpagesByPlatform,
    location.pathname,
    fanpagesLoading,
    facebookPages.length,
  ]);

  useEffect(() => {
    const isSettingsActive = location.pathname.startsWith("/settings");
    const currentActiveKeys = activeKeys;
    if (isSettingsActive && prevActiveKeysRef.current.length > 0) {
      setManuallyExpandedKeys((prev) => {
        const newSet = new Set(prev);
        prevActiveKeysRef.current.forEach((key) => {
          if (!newSet.has(key)) {
            newSet.add(key);
          }
        });
        return newSet;
      });
    }

    if (!isSettingsActive) {
      prevActiveKeysRef.current = currentActiveKeys;
    }
  }, [location.pathname, activeKeys]);

  const activeSettingMenuItem = useMemo(() => {
    if (location.pathname.startsWith("/settings/")) {
      const settingGroup = PLATFORM_GROUPS.find((g) => g.id === "setting");
      const menuItem = settingGroup?.submenu?.find(
        (item) =>
          location.pathname === item.path ||
          location.pathname.startsWith(`${item.path}/`)
      );
      return menuItem?.id || null;
    }
    return null;
  }, [location.pathname]);

  const expandedKeys = useMemo(() => {
    const keys = new Set<string>();
    activeKeys.forEach((key) => keys.add(key));
    manuallyExpandedKeys.forEach((key) => {
      if (!activeKeys.includes(key)) {
        keys.add(key);
      }
    });
    if (selectedFanpageGroupId && !keys.has(selectedFanpageGroupId)) {
      keys.add(selectedFanpageGroupId);
    }
    return Array.from(keys);
  }, [activeKeys, manuallyExpandedKeys, selectedFanpageGroupId]);

  const toggleGroup = useCallback(
    (groupKey: string) => {
      if (activeKeys.includes(groupKey)) {
        return;
      }

      // Fetch data when expanding group
      if (groupKey === PLATFORM_IDS.FACEBOOK && facebookPages.length === 0) {
        dispatch(fetchFacebookPages());
      } else if (groupKey === PLATFORM_IDS.ZALO && zaloOAs.length === 0) {
        dispatch(fetchZaloOAs());
      }

      setManuallyExpandedKeys((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(groupKey)) {
          newSet.delete(groupKey);
        } else {
          newSet.add(groupKey);
        }
        return newSet;
      });
    },
    [activeKeys, dispatch, facebookPages.length, zaloOAs.length]
  );

  const handleChannelClick = useCallback(
    async (channel: FanpageItem, groupId: string) => {
      void groupId;

      if (
        selectedFanpageName === channel.name &&
        selectedChannel === channel.channelType &&
        !location.pathname.startsWith("/settings")
      ) {
        return;
      }

      setIsSwitchingFanpage(true);

      try {
        selectConversation(undefined);

        const messagesStore = store.getState().messages;
        Object.keys(messagesStore.messagesByConversation).forEach((convId) => {
          dispatch(clearMessages(convId));
        });

        const clearConversations = useChatStore.getState().clearConversations;
        clearConversations();

        if (channel.channelType === CHANNELS.ZALO && zaloOAs.length === 0) {
          await dispatch(fetchZaloOAs());
        }

        selectFanpage(channel.name, channel.id, channel.channelType);

        const newUrl = "/conversations";
        window.history.replaceState(null, "", newUrl);

        navigate(newUrl, { replace: true });
      } finally {
        setIsSwitchingFanpage(false);
      }
    },
    [
      dispatch,
      selectConversation,
      selectFanpage,
      setIsSwitchingFanpage,
      navigate,
      zaloOAs.length,
      selectedFanpageName,
      selectedChannel,
      location.pathname,
    ]
  );

  const renderChannelContent = (group: (typeof PLATFORM_GROUPS)[0]) => {
    if (group.submenu && group.submenu.length > 0) {
      return (
        <div className={styles.channels}>
          {group.submenu.map((item) => {
            const isActive = activeSettingMenuItem === item.id;
            return (
              <Button
                key={item.id}
                type="text"
                className={classNames(styles.channel, {
                  [styles.active]: isActive,
                })}
                onClick={() => {
                  navigate(item.path);
                  item.onClick?.();
                }}
                block
              >
                <span className={styles.channelName}>{item.label}</span>
              </Button>
            );
          })}
        </div>
      );
    }

    const fanpages = fanpagesByPlatform[group.id] || [];
    const isLoading =
      (group.id === PLATFORM_IDS.FACEBOOK && fanpagesLoading) ||
      (group.id === PLATFORM_IDS.ZALO && zaloOAsLoading);

    if (isLoading) return <div className={styles.empty}>Đang tải...</div>;
    if (fanpages.length === 0)
      return <div className={styles.empty}>Chưa có kênh nào</div>;

    return (
      <div className={styles.channels}>
        {fanpages.map((fanpage) => {
          const isSelected =
            !location.pathname.startsWith("/settings") &&
            selectedFanpageName === fanpage.name &&
            selectedChannel === fanpage.channelType;

          return (
            <Button
              key={fanpage.id}
              type="text"
              className={classNames(styles.channel, {
                [styles.active]: isSelected,
              })}
              onClick={() => handleChannelClick(fanpage, group.id)}
              block
            >
              <Tooltip title={fanpage.name} placement="right">
                <span className={styles.channelName}>{fanpage.name}</span>
              </Tooltip>
              {(fanpage.messageSyncStatus === "NOT_SYNCED" ||
                fanpage.messageSyncStatus === "IN_PROGRESS") && (
                <span className={styles.syncIcon}>
                  {fanpage.messageSyncStatus === "NOT_SYNCED" ? (
                    <SyncOutlined />
                  ) : (
                    <SyncOutlined spin />
                  )}
                </span>
              )}
              {fanpage.unreadCount > 0 && (
                <span className={styles.badge}>
                  {formatUnreadCount(fanpage.unreadCount)}
                </span>
              )}
            </Button>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.channelMenuWrapper}>
      <div
        className={classNames(styles.channelMenu, {
          [styles.collapsed]: isCollapsed,
        })}
      >
        {PLATFORM_GROUPS.map((group) => {
          const isExpanded = expandedKeys.includes(group.id);

          return (
            <div key={group.id} className={styles.group}>
              <div
                className={classNames(styles.groupHeader, {
                  [styles.collapsed]: isCollapsed,
                })}
              >
                <div
                  className={styles.groupLabel}
                  onClick={(e) => {
                    if (isCollapsed) handleToggleFanpageMenu?.(e);
                    toggleGroup(group.id);
                  }}
                >
                  {group.icon}
                  {!isCollapsed && <span>{group.label}</span>}
                </div>
                {!isCollapsed && (
                  <RightOutlined
                    className={classNames(styles.expandIcon, {
                      [styles.expanded]: isExpanded,
                    })}
                  />
                )}
              </div>
              {!isCollapsed && (
                <div
                  className={classNames(styles.groupContent, {
                    [styles.expanded]: isExpanded,
                  })}
                >
                  <div>{renderChannelContent(group)}</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
